import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import {
  FiCheck,
  FiChevronLeft,
  FiUser,
  FiCreditCard,
  FiUpload,
  FiX,
  FiAlertCircle,
  FiArrowRight,
  FiPhone,
} from 'react-icons/fi';
import { RiBuilding2Line } from 'react-icons/ri';
import { HiOutlineDocumentText } from 'react-icons/hi';
import supplierService from '../../../services/supplierService';
import ifaService from '../../../services/ifaService';
import { processSupportingDocumentForUpload } from '../../../utils/cmrFileProcessing';
import {
  applyBankManualUploadState,
  buildBankManualUploadState,
} from '../../../utils/bankVerifyManualUpload';
import BankManualUploadErrorAlert from '../../../components/bank/BankManualUploadErrorAlert';
import DashboardPageWrapper from '../../../components/layout/DashboardPageWrapper';
import {
  DEMAT_FORMAT_HINT,
  DEMAT_MAX_LENGTH,
  processDematInputChange,
  validateDematNumber,
} from '../../../utils/dematValidation';

const LIST_PATH = '/supplier-masters';
const ONBOARD_PATH = `${LIST_PATH}/onboard`;

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const WIZARD_STEPS = [
  { key: 'STEP_1_PAN', label: 'PAN Verification', sublabel: 'Verify PAN', icon: FiCreditCard },
  { key: 'STEP_2_DETAILS', label: 'Personal Details', sublabel: 'Email & phone', icon: FiUser },
  { key: 'STEP_3_BANK', label: 'Bank Details', sublabel: 'Account details', icon: RiBuilding2Line },
  { key: 'STEP_4_DEMAT', label: 'Demat Details', sublabel: 'DP & demat ID', icon: FiCreditCard },
  { key: 'STEP_5_CML', label: 'CML Document', sublabel: 'Upload document', icon: HiOutlineDocumentText },
  { key: 'COMPLETED', label: 'Completed', sublabel: 'Ready', icon: FiCheck },
];

/** When edit mode + onboarding already complete, open first fully editable step (bank). */
const EDIT_COMPLETE_DEFAULT_STEP = 'STEP_3_BANK';

function extractSupplierId(data) {
  if (!data || typeof data !== 'object') return null;
  const id = data.id ?? data.supplier_id ?? data.user_id;
  return id != null && id !== '' ? String(id) : null;
}

function isStepCompleted(status, stepKey) {
  const steps = status?.onboarding_steps;
  if (!Array.isArray(steps)) return false;
  const row = steps.find((s) => s.key === stepKey);
  return Boolean(row?.completed);
}

function isOnboardingFinished(status) {
  if (!status) return false;
  if (status.resume_at_step === 'COMPLETED') return true;
  const doneRow = status.onboarding_steps?.find((s) => s.key === 'COMPLETED');
  if (doneRow?.completed) return true;
  const st = String(status.onboarding_status || '').toUpperCase();
  if (st === 'COMPLETED' || st === 'ONBOARDING_COMPLETE' || st === 'ONBOARDED') return true;
  return false;
}

function serverStepKeyFromStatus(status) {
  if (!status) return 'STEP_1_PAN';
  if (isOnboardingFinished(status)) return 'COMPLETED';
  return status.resume_at_step || status.onboarding_step || 'STEP_1_PAN';
}

function buildCompletedKeys(status) {
  const steps = status?.onboarding_steps;
  if (!Array.isArray(steps)) return [];
  return steps.filter((s) => s.completed && s.key !== 'COMPLETED').map((s) => s.key);
}

function InputField({ label, error, className = '', children }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
      {children}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <FiAlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
}

function StyledInput({ value, onChange, placeholder, disabled, className = '', type = 'text', maxLength, autoFocus }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      autoFocus={autoFocus}
      className={`w-full px-3.5 py-2.5 rounded-lg border text-sm transition-all outline-none
        ${disabled
          ? 'bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed'
          : 'bg-white text-gray-900 border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'
        } ${className}`}
    />
  );
}

function GreenButton({ onClick, disabled, loading, children, className = '', type = 'button' }) {
  const inactive = disabled || loading;
  const stateClass = loading
    ? 'bg-green-600 text-white cursor-wait shadow-sm'
    : disabled
      ? 'bg-gray-200 text-gray-500 cursor-not-allowed shadow-none'
      : 'bg-green-600 text-white hover:bg-green-700 active:scale-[0.99] shadow-sm hover:shadow-md';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={inactive}
      className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all ${stateClass} ${className}`}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" aria-hidden>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          Processing...
        </span>
      ) : (
        children
      )}
    </button>
  );
}

function WizardSidebar({
  completedStepKeys,
  currentStepKey,
  maxReachedKey,
  contextLabel,
  percentComplete,
  onStepClick,
  wizardTitle = 'Supplier onboarding',
}) {
  const maxIdx = WIZARD_STEPS.findIndex((s) => s.key === maxReachedKey);

  return (
    <div className="flex flex-col h-auto min-h-0 lg:h-full">
      <div className="mb-6 lg:mb-8">
        <p className="text-xs font-semibold text-green-600 tracking-widest uppercase mb-2">Admin Dashboard</p>
        <h2 className="text-xl font-bold text-gray-900 leading-tight">{wizardTitle}</h2>
        {contextLabel && (
          <div className="mt-3 px-3 py-2 bg-green-50 rounded-lg border border-green-100">
            <p className="text-xs text-gray-500">Supplier</p>
            <p className="text-sm font-medium text-gray-800 truncate">{contextLabel}</p>
          </div>
        )}
      </div>

      {percentComplete > 0 && (
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-1.5">
            <span>Progress</span>
            <span>{percentComplete}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all duration-700"
              style={{ width: `${Math.min(100, percentComplete)}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1 lg:flex-1">
        {WIZARD_STEPS.map((step, idx) => {
          const isCompleted = completedStepKeys.includes(step.key);
          const isActive = step.key === currentStepKey;
          const isPending = !isCompleted && !isActive;
          const isClickable = onStepClick && maxIdx >= 0 && idx <= maxIdx;

          const inner = (
            <>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-all
                  ${isCompleted ? 'bg-green-600 text-white' : ''}
                  ${isActive ? 'bg-green-600 text-white ring-4 ring-green-100' : ''}
                  ${isPending ? 'bg-gray-100 text-gray-400 border-2 border-gray-200' : ''}
                `}
              >
                {isCompleted ? <FiCheck size={14} /> : idx + 1}
              </div>
              <div className="min-w-0">
                <p
                  className={`text-sm font-semibold leading-tight ${
                    isActive ? 'text-green-700' : isCompleted ? 'text-gray-600' : 'text-gray-400'
                  }`}
                >
                  {step.label}
                </p>
                <p className={`text-xs mt-0.5 ${isActive ? 'text-green-600' : 'text-gray-400'}`}>{step.sublabel}</p>
              </div>
              {isCompleted && (
                <div className="ml-auto">
                  <FiCheck size={14} className="text-green-500" />
                </div>
              )}
            </>
          );

          if (isClickable) {
            return (
              <button
                key={step.key}
                type="button"
                onClick={() => onStepClick(step.key)}
                className={`group flex items-center gap-3 px-3 py-3 rounded-xl border transition-all w-full text-left
                  ${isActive ? 'bg-green-50 border-green-200' : 'border-transparent hover:bg-gray-50 hover:border-gray-200/80'}
                  ${isCompleted && !isActive ? 'opacity-90' : ''}
                `}
                title={step.label}
              >
                {inner}
              </button>
            );
          }

          return (
            <div
              key={step.key}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all
                ${isActive ? 'bg-green-50 border border-green-200' : ''}
                ${isCompleted ? 'opacity-90' : ''}
              `}
            >
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OnboardingWizardLayout({
  children,
  completedStepKeys,
  currentStepKey,
  maxReachedKey,
  contextLabel,
  percentComplete,
  onBackToList,
  onStepClick,
  wizardTitle,
}) {
  return (
    <DashboardPageWrapper>
      <button
        type="button"
        onClick={onBackToList}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-5 transition-colors"
      >
        <FiChevronLeft size={16} /> Back to supplier list
      </button>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="grid grid-cols-1 items-start lg:grid-cols-[280px_1fr] lg:items-stretch">
          <div className="bg-white border-b lg:border-b-0 lg:border-r border-gray-100 p-7 w-full">
            <WizardSidebar
              completedStepKeys={completedStepKeys}
              currentStepKey={currentStepKey}
              maxReachedKey={maxReachedKey}
              contextLabel={contextLabel}
              percentComplete={percentComplete}
              onStepClick={onStepClick}
              wizardTitle={wizardTitle}
            />
          </div>

          <div className="flex w-full min-h-0 h-auto items-start justify-center p-7 pb-8 lg:min-h-[520px] lg:items-center lg:justify-center lg:p-10">
            <div className="w-full max-w-lg shrink-0">{children}</div>
          </div>
        </div>
      </div>
    </DashboardPageWrapper>
  );
}

function SupplierPanStep({ status, onCreatedId, onSuccess, isEditMode = false }) {
  const [pan, setPan] = useState(status?.pan || '');
  const [lookupName, setLookupName] = useState(status?.name || '');
  const [panLookupLoading, setPanLookupLoading] = useState(false);
  const [panVerified, setPanVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const panValue = pan.toUpperCase();
  const isPanValid = PAN_REGEX.test(panValue);
  const panDone = isStepCompleted(status, 'STEP_1_PAN');
  const panFrozen = (isEditMode && Boolean(status?.pan)) || panDone;

  useEffect(() => {
    if ((panDone || (isEditMode && status?.pan)) && status?.pan) {
      setPan(status.pan);
      setLookupName(status.name || '');
      setPanVerified(true);
      return;
    }
    if (!isPanValid) {
      setLookupName('');
      setPanVerified(false);
      return;
    }
    let cancelled = false;
    const lookup = async () => {
      setPanLookupLoading(true);
      try {
        const res = await ifaService.lookupPAN(panValue);
        if (!cancelled) {
          setLookupName(res.data?.name || '');
          setPanVerified(true);
        }
      } catch {
        if (!cancelled) {
          setLookupName('');
          setPanVerified(false);
        }
      } finally {
        if (!cancelled) setPanLookupLoading(false);
      }
    };
    lookup();
    return () => {
      cancelled = true;
    };
  }, [panValue, isPanValid, panDone, isEditMode, status?.pan, status?.name]);

  const handleSubmit = async () => {
    if (panFrozen) {
      onSuccess();
      return;
    }
    if (!isPanValid) {
      setErrors({ pan: 'Enter a valid PAN (e.g. ABCDE1234F)' });
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const data = await supplierService.step1Pan({ pan: panValue });
      const newId = extractSupplierId(data);
      toast.success('PAN saved.');
      if (newId) {
        if (onCreatedId) onCreatedId(newId);
        else onSuccess();
      } else {
        toast.error('Supplier was saved but no id was returned. Check the API response shape.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save PAN.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">PAN verification</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          PAN is verified with the registrar; name is shown when available.
        </p>
      </div>

      <div className="flex flex-col gap-5">
        <InputField label="PAN number" error={errors.pan}>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <FiCreditCard size={16} />
            </div>
            <input
              value={panValue}
              onChange={(e) => {
                setPan(e.target.value.toUpperCase());
                setErrors((p) => ({ ...p, pan: '' }));
              }}
              placeholder="ABCDE1234F"
              maxLength={10}
              disabled={panFrozen}
              className={`w-full pl-9 pr-10 py-2.5 rounded-lg border text-sm transition-all outline-none uppercase
                ${panFrozen ? 'bg-gray-50 text-gray-500' : ''}
                ${errors.pan ? 'border-red-400' : 'border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'}`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {panLookupLoading && (
                <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              )}
              {!panLookupLoading && panVerified && <FiCheck size={16} className="text-green-500" />}
            </div>
          </div>
          {panVerified && lookupName && (
            <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
              <FiCheck size={12} /> Name on PAN: <span className="font-medium">{lookupName}</span>
            </p>
          )}
        </InputField>

        {lookupName && (
          <InputField label="Name (as per PAN)">
            <StyledInput value={lookupName} disabled placeholder="From PAN lookup" />
          </InputField>
        )}

        {panFrozen && (
          <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
            {isEditMode
              ? 'PAN is locked for this supplier. Use the sidebar to update bank, demat, or CML.'
              : 'This step is already completed. Use the sidebar to open another step, or return to the current step.'}
          </p>
        )}

        <GreenButton onClick={handleSubmit} disabled={!panFrozen && !isPanValid} loading={loading}>
          {panFrozen ? 'Done' : 'Continue'} <FiArrowRight className="inline ml-1" />
        </GreenButton>
      </div>
    </div>
  );
}

function SupplierPersonalStep({ supplierId, status, onSuccess, lockEmailPhone = false }) {
  const pre = status?.personal_details_prefill || {};
  const [email, setEmail] = useState(pre.email || status?.email || '');
  const [phone, setPhone] = useState(pre.phone || status?.phone || '');
  const [dob, setDob] = useState(
    pre.date_of_birth ? dayjs(pre.date_of_birth).format('YYYY-MM-DD') : status?.date_of_birth
      ? dayjs(status.date_of_birth).format('YYYY-MM-DD')
      : ''
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!lockEmailPhone) {
      if (!email.trim()) e.email = 'Email is required';
      else if (!EMAIL_REGEX.test(email.trim())) e.email = 'Invalid email';
      if (!phone || !/^\d{10}$/.test(phone)) e.phone = 'Enter a valid 10-digit mobile number';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await supplierService.step2Personal(supplierId, {
        email: email.trim(),
        phone,
        date_of_birth: dob || null,
      });
      toast.success('Personal details saved.');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save personal details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Personal details</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          {lockEmailPhone
            ? 'Email and phone are locked. Date of birth is optional.'
            : 'Email, phone, and optional date of birth for the supplier.'}
        </p>
      </div>

      <div className="flex flex-col gap-5">
        <InputField label="Email" error={errors.email}>
          <StyledInput
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((p) => ({ ...p, email: '' }));
            }}
            placeholder="supplier@example.com"
            disabled={lockEmailPhone}
          />
        </InputField>

        <InputField label="Phone" error={errors.phone}>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <FiPhone size={16} />
            </div>
            <input
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value.replace(/\D/g, ''));
                setErrors((p) => ({ ...p, phone: '' }));
              }}
              placeholder="10-digit mobile number"
              maxLength={10}
              disabled={lockEmailPhone}
              className={`w-full pl-9 py-2.5 rounded-lg border text-sm transition-all outline-none
                ${lockEmailPhone ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}
                ${errors.phone ? 'border-red-400' : 'border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'}`}
            />
          </div>
        </InputField>

        <InputField label="Date of birth (optional)" error={errors.dob}>
          <DatePicker
            size="large"
            className="w-full min-w-0"
            value={dob ? dayjs(dob, 'YYYY-MM-DD') : null}
            onChange={(d) => {
              setDob(d ? d.format('YYYY-MM-DD') : '');
              setErrors((p) => ({ ...p, dob: '' }));
            }}
            format="DD/MM/YYYY"
            placeholder="Optional"
            disabledDate={(current) => current && current > dayjs().endOf('day')}
            status={errors.dob ? 'error' : undefined}
          />
        </InputField>

        <GreenButton onClick={handleSubmit} loading={loading}>
          Continue <FiArrowRight className="inline ml-1" />
        </GreenButton>
      </div>
    </div>
  );
}

function mapPoolBanks(pools) {
  const raw = pools?.bank_accounts;
  if (!Array.isArray(raw)) return [];
  return raw.map((b) => ({
    id: String(b.id ?? b.uuid ?? ''),
    account_number: b.account_number,
    ifsc_code: b.ifsc_code,
    bank_name: b.bank_name,
    branch_name: b.branch_name,
    is_verified: b.is_verified,
  })).filter((b) => b.id);
}

function mapPoolDemats(pools) {
  const raw = pools?.demat_accounts;
  if (!Array.isArray(raw)) return [];
  return raw.map((d) => ({
    id: String(d.id ?? d.uuid ?? ''),
    demat_number: d.demat_number ?? d.demat_account_no ?? d.account_number,
    dp_name: d.dp_name ?? d.depository_participant_name ?? '',
  })).filter((d) => d.demat_number);
}

function isPreviewImageFile(file) {
  if (!file) return false;
  const mime = String(file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const name = String(file.name || '').trim().toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(name);
}

function UploadedFilePreview({ file }) {
  const previewUrl = useMemo(() => {
    if (!file || !isPreviewImageFile(file)) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      {previewUrl ? (
        <div className="w-full rounded-lg overflow-hidden border border-green-200/80 bg-gray-50 max-h-52 flex items-center justify-center">
          <img
            src={previewUrl}
            alt={`Preview: ${file.name}`}
            className="max-h-52 w-full object-contain"
          />
        </div>
      ) : (
        <div
          className="w-20 h-20 rounded-xl bg-green-100 border border-green-200/70 flex items-center justify-center"
          aria-hidden
        >
          <HiOutlineDocumentText className="w-10 h-10 text-green-700" />
        </div>
      )}
      <div className="text-center min-w-0 w-full">
        <p className="text-sm text-gray-700 font-medium break-all">{file.name}</p>
        <p className="text-xs text-gray-400 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
      </div>
    </div>
  );
}

function SupplierBankStep({ supplierId, status, onSuccess }) {
  const pools = status?.existing_pools || {};
  const poolBanks = mapPoolBanks(pools);
  const hasPrefill = poolBanks.length > 0;
  const phone =
    status?.personal_details_prefill?.phone ||
    status?.phone ||
    '';

  const [bankMode, setBankMode] = useState(hasPrefill ? 'existing' : 'mobile');
  const [selectedPoolId, setSelectedPoolId] = useState(hasPrefill ? poolBanks[0].id : '');

  const [mobileBankData, setMobileBankData] = useState(null);
  const [mobileBankLoading, setMobileBankLoading] = useState(false);
  const [mobileBankError, setMobileBankError] = useState('');

  const [accountNumber, setAccountNumber] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [bankName, setBankName] = useState('');
  const [branchName, setBranchName] = useState('');
  const [ifscLookupLoading, setIfscLookupLoading] = useState(false);
  const [ifscVerified, setIfscVerified] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [mismatchData, setMismatchData] = useState(null);
  const [chequeFile, setChequeFile] = useState(null);
  const [otherDocFile, setOtherDocFile] = useState(null);
  const [chequeFileProcessing, setChequeFileProcessing] = useState(false);
  const [otherDocFileProcessing, setOtherDocFileProcessing] = useState(false);
  const chequeFileInputRef = useRef(null);
  const otherDocFileInputRef = useRef(null);

  const mobileDigits = /^\d{10}$/.test(String(phone).replace(/\D/g, '')) ? String(phone).replace(/\D/g, '') : '';
  const supplierSubjectUserId =
    status?.bank_from_mobile_user_id ||
    status?.user_uuid ||
    status?.uuid ||
    status?.user_id ||
    supplierId ||
    '';
  const supplierPanVerifiedName =
    status?.pan_verified_name ||
    status?.name ||
    status?.personal_details_prefill?.name ||
    '';

  useEffect(() => {
    if (!mobileDigits) return;
    setMobileBankLoading(true);
    setMobileBankError('');
    ifaService
      .getBankFromMobile(
        mobileDigits,
        supplierPanVerifiedName || undefined,
        supplierSubjectUserId || undefined
      )
      .then((res) => {
        setMobileBankData(res.data?.suggested_bank || null);
      })
      .catch(() => {
        setMobileBankError('Could not fetch bank details linked to mobile number.');
      })
      .finally(() => setMobileBankLoading(false));
  }, [mobileDigits, supplierPanVerifiedName, supplierSubjectUserId]);

  const ifscValue = ifsc.toUpperCase();
  useEffect(() => {
    if (bankMode !== 'manual') return;
    if (!IFSC_REGEX.test(ifscValue)) {
      setBankName('');
      setBranchName('');
      setIfscVerified(false);
      return;
    }
    let cancelled = false;
    const lookup = async () => {
      setIfscLookupLoading(true);
      try {
        const res = await ifaService.lookupIFSC(ifscValue);
        if (!cancelled) {
          setBankName(res.data?.bank_name || '');
          setBranchName(res.data?.branch_name || '');
          setIfscVerified(true);
        }
      } catch {
        if (!cancelled) {
          setBankName('');
          setBranchName('');
          setIfscVerified(false);
        }
      } finally {
        if (!cancelled) setIfscLookupLoading(false);
      }
    };
    lookup();
    return () => {
      cancelled = true;
    };
  }, [ifscValue, bankMode]);

  const selectedBank = poolBanks.find((b) => b.id === selectedPoolId);

  const validate = () => {
    const e = {};
    if (bankMode === 'existing' && !selectedPoolId) e.general = 'Please select a bank account';
    else if (bankMode === 'manual') {
      if (!accountNumber.trim()) e.accountNumber = 'Account number is required';
      if (!IFSC_REGEX.test(ifscValue)) e.ifsc = 'Enter a valid IFSC code';
      if (!bankName.trim()) e.bankName = 'Bank name is required (use IFSC lookup)';
    } else if (bankMode === 'mobile' && !mobileBankData) {
      e.general = mobileDigits
        ? 'Mobile-linked bank data not available. Please use manual entry.'
        : 'Add a valid 10-digit phone on the previous step or use manual entry.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const getAttemptedBankFields = () => {
    if (bankMode === 'existing' && selectedBank) {
      return {
        accountNumber: selectedBank.account_number,
        ifsc: selectedBank.ifsc_code,
        bankName: selectedBank.bank_name,
      };
    }
    if (bankMode === 'mobile' && mobileBankData) {
      return {
        accountNumber: mobileBankData.account_number,
        ifsc: mobileBankData.ifsc_code,
        bankName: mobileBankData.bank_name,
        branchName: mobileBankData.branch_name || '',
      };
    }
    return {
      accountNumber,
      ifsc: ifscValue,
      bankName,
      branchName,
    };
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      let payload;
      if (bankMode === 'existing' && selectedBank) {
        payload = {
          bank_name: selectedBank.bank_name,
          account_number: selectedBank.account_number,
          ifsc_code: selectedBank.ifsc_code,
        };
      } else if (bankMode === 'mobile' && mobileBankData) {
        payload = {
          bank_name: mobileBankData.bank_name,
          account_number: mobileBankData.account_number,
          ifsc_code: mobileBankData.ifsc_code,
        };
      } else {
        payload = {
          bank_name: bankName.trim(),
          account_number: accountNumber.trim(),
          ifsc_code: ifscValue,
        };
      }
      await supplierService.step3Bank(supplierId, payload);
      toast.success('Bank details saved.');
      onSuccess();
    } catch (err) {
      const manualUpload = buildBankManualUploadState(err, getAttemptedBankFields());
      applyBankManualUploadState(manualUpload, {
        setAccountNumber,
        setIfsc,
        setBankName,
        setBranchName,
        setBankMode,
        setMismatchData,
        setErrors,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMismatchSubmit = async () => {
    if (!chequeFile) {
      setErrors({ cheque: 'Cancelled cheque / supporting document is required' });
      return;
    }
    if (chequeFileProcessing || otherDocFileProcessing) return;
    if (!accountNumber.trim()) {
      setErrors((p) => ({ ...p, accountNumber: 'Account number is required' }));
      return;
    }
    if (!IFSC_REGEX.test(ifscValue)) {
      setErrors((p) => ({ ...p, ifsc: 'Enter a valid IFSC code' }));
      return;
    }
    if (!bankName.trim()) {
      setErrors((p) => ({ ...p, bankName: 'Bank name is required (use IFSC lookup)' }));
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('account_number', accountNumber.trim());
      fd.append('ifsc_code', ifscValue);
      fd.append('bank_name', bankName.trim());
      fd.append('branch_name', branchName || '');
      fd.append('cheque', chequeFile);
      if (otherDocFile) fd.append('supporting_document', otherDocFile);
      await supplierService.uploadBankMismatchCheque(supplierId, fd);
      toast.success('Documents submitted for review!');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to upload documents.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBackFromMismatch = () => {
    setMismatchData(null);
    setChequeFile(null);
    setOtherDocFile(null);
    setChequeFileProcessing(false);
    setOtherDocFileProcessing(false);
    setErrors({});
  };

  const applyChequeFile = async (file) => {
    if (!file) return;
    setChequeFileProcessing(true);
    try {
      const result = await processSupportingDocumentForUpload(file);
      if (!result.ok) {
        toast.error(result.error);
        setErrors((p) => ({ ...p, cheque: result.error }));
        return;
      }
      setChequeFile(result.file);
      setErrors((p) => ({ ...p, cheque: '' }));
      if (result.wasCompressed) {
        toast.success('Image compressed to under 2 MB.');
      }
    } finally {
      setChequeFileProcessing(false);
    }
  };

  const applyOtherDocFile = async (file) => {
    if (!file) return;
    setOtherDocFileProcessing(true);
    try {
      const result = await processSupportingDocumentForUpload(file);
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      setOtherDocFile(result.file);
      if (result.wasCompressed) {
        toast.success('Image compressed to under 2 MB.');
      }
    } finally {
      setOtherDocFileProcessing(false);
    }
  };

  const bankOptions = [];
  if (hasPrefill) bankOptions.push({ value: 'existing', label: 'Use an existing bank account from profile' });
  bankOptions.push({ value: 'mobile', label: 'Use primary account linked to supplier phone' });
  bankOptions.push({ value: 'manual', label: 'Enter account manually' });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Bank details</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          {mismatchData
            ? 'Bank verification could not be completed automatically. Upload documents for manual review.'
            : 'Mobile-linked account, existing account, or manual IFSC entry.'}
        </p>
      </div>

      {mismatchData ? (
        <div className="flex flex-col gap-5">
          <BankManualUploadErrorAlert manualUploadData={mismatchData} />

          <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
            <InputField label="Account number" error={errors.accountNumber}>
              <StyledInput
                value={accountNumber}
                onChange={(e) => {
                  setAccountNumber(e.target.value);
                  setErrors((p) => ({ ...p, accountNumber: '' }));
                }}
                placeholder="Enter account number"
              />
            </InputField>

            <InputField label="IFSC" error={errors.ifsc}>
              <div className="relative">
                <input
                  value={ifscValue}
                  onChange={(e) => {
                    setIfsc(e.target.value.toUpperCase());
                    setErrors((p) => ({ ...p, ifsc: '' }));
                  }}
                  placeholder="e.g. HDFC0001234"
                  maxLength={11}
                  className={`w-full px-3.5 pr-10 py-2.5 rounded-lg border text-sm transition-all outline-none uppercase
                    ${errors.ifsc ? 'border-red-400' : 'border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'}`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {ifscLookupLoading && (
                    <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  )}
                  {!ifscLookupLoading && ifscVerified && <FiCheck size={16} className="text-green-500" />}
                </div>
              </div>
            </InputField>

            <InputField label="Bank name" error={errors.bankName}>
              <StyledInput
                value={bankName}
                placeholder={ifscLookupLoading ? 'Fetching from IFSC…' : 'Auto-filled from IFSC lookup'}
                disabled
              />
            </InputField>

            {branchName && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-3.5 py-2.5 text-sm text-green-700">
                Branch: <span className="font-medium">{branchName}</span>
              </div>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              Cancelled cheque / supporting document <span className="text-red-500">*</span>
            </p>
            {errors.cheque && (
              <p className="text-xs text-red-500 flex items-center gap-1 mb-1">
                <FiAlertCircle size={12} /> {errors.cheque}
              </p>
            )}
            <div
              role="button"
              tabIndex={0}
              onClick={() => !loading && !chequeFileProcessing && chequeFileInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && !chequeFileProcessing && chequeFileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center gap-2 transition-all
                ${chequeFileProcessing ? 'border-gray-200 bg-gray-50 cursor-wait opacity-80' : 'cursor-pointer'}
                ${!chequeFileProcessing && (chequeFile ? 'border-green-400 bg-green-50/50' : 'border-gray-200 bg-gray-50 hover:border-green-400 hover:bg-green-50/30')}`}
            >
              {chequeFileProcessing ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <p className="text-sm text-gray-500">Processing file...</p>
                </>
              ) : chequeFile ? (
                <UploadedFilePreview file={chequeFile} />
              ) : (
                <>
                  <FiUpload size={20} className="text-gray-400" />
                  <p className="text-sm text-gray-500">Click to select file</p>
                  <p className="text-xs text-gray-400">PDF max 2 MB · Images up to 5 MB (auto-compressed to 2 MB)</p>
                </>
              )}
            </div>
            <input
              ref={chequeFileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={chequeFileProcessing}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) applyChequeFile(f);
              }}
            />
            {chequeFile && !chequeFileProcessing && (
              <button
                type="button"
                onClick={() => setChequeFile(null)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FiX size={12} /> Remove file
              </button>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Other supporting documents (optional)</p>
            <div
              role="button"
              tabIndex={0}
              onClick={() => !loading && !otherDocFileProcessing && otherDocFileInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && !otherDocFileProcessing && otherDocFileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center gap-2 transition-all
                ${otherDocFileProcessing ? 'border-gray-200 bg-gray-50 cursor-wait opacity-80' : 'cursor-pointer'}
                ${!otherDocFileProcessing && (otherDocFile ? 'border-green-400 bg-green-50/50' : 'border-gray-200 bg-gray-50 hover:border-green-400 hover:bg-green-50/30')}`}
            >
              {otherDocFileProcessing ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <p className="text-sm text-gray-500">Processing file...</p>
                </>
              ) : otherDocFile ? (
                <UploadedFilePreview file={otherDocFile} />
              ) : (
                <>
                  <FiUpload size={20} className="text-gray-400" />
                  <p className="text-sm text-gray-500">Click to select file</p>
                  <p className="text-xs text-gray-400">PDF max 2 MB · Images up to 5 MB (auto-compressed to 2 MB)</p>
                </>
              )}
            </div>
            <input
              ref={otherDocFileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={otherDocFileProcessing}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) applyOtherDocFile(f);
              }}
            />
            {otherDocFile && !otherDocFileProcessing && (
              <button
                type="button"
                onClick={() => setOtherDocFile(null)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FiX size={12} /> Remove file
              </button>
            )}
          </div>

          <GreenButton
            onClick={handleMismatchSubmit}
            loading={loading}
            disabled={
              chequeFileProcessing ||
              otherDocFileProcessing ||
              !accountNumber.trim() ||
              !ifscVerified ||
              !chequeFile
            }
          >
            Submit for Review
          </GreenButton>

          <button
            type="button"
            onClick={handleGoBackFromMismatch}
            className="text-sm text-gray-500 hover:text-gray-700 text-center"
          >
            Go back to bank verification
          </button>
        </div>
      ) : (
        <>
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-700">Supplier bank account</p>
          <RiBuilding2Line size={20} className="text-green-600" />
        </div>
        <div className="flex flex-col gap-2.5">
          {bankOptions.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2.5 cursor-pointer group">
              <div
                role="button"
                tabIndex={0}
                onClick={() => setBankMode(opt.value)}
                onKeyDown={(e) => e.key === 'Enter' && setBankMode(opt.value)}
                className={`rounded-full border-2 flex items-center justify-center transition-all cursor-pointer
                  ${bankMode === opt.value ? 'border-green-600 bg-green-600' : 'border-gray-400 bg-white group-hover:border-green-400'}`}
                style={{ width: 18, height: 18 }}
              >
                {bankMode === opt.value && <div className="w-2 h-2 bg-white rounded-full" />}
              </div>
              <span
                role="button"
                tabIndex={0}
                onClick={() => setBankMode(opt.value)}
                onKeyDown={(e) => e.key === 'Enter' && setBankMode(opt.value)}
                className={`text-sm cursor-pointer ${bankMode === opt.value ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
              >
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2 text-sm text-red-600">
          <FiAlertCircle size={16} className="shrink-0 mt-0.5" /> {errors.general}
        </div>
      )}

      {bankMode === 'existing' && (
        <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
          <InputField label="Select existing bank account">
            <Select
              className="w-full"
              size="large"
              value={selectedPoolId}
              onChange={setSelectedPoolId}
              options={poolBanks.map((b) => ({
                value: b.id,
                label: `${b.bank_name} — ${b.account_number} (${b.ifsc_code})`,
              }))}
            />
          </InputField>
          {selectedBank?.is_verified && (
            <p className="text-xs text-green-600 flex items-center gap-1">
              <FiCheck size={12} /> Verified account
            </p>
          )}
        </div>
      )}

      {bankMode === 'mobile' && (
        <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
          {!mobileDigits && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
              Phone from personal details is missing or invalid. Switch to manual entry.
            </p>
          )}
          {mobileBankLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Fetching bank details linked to phone…
            </div>
          )}
          {mobileBankError && !mobileBankLoading && mobileDigits && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 flex gap-2">
              <FiAlertCircle size={16} className="shrink-0 mt-0.5" /> {mobileBankError}
            </div>
          )}
          {mobileBankData && !mobileBankLoading && (
            <>
              <InputField label="Account number">
                <StyledInput value={mobileBankData.account_number} disabled />
              </InputField>
              <InputField label="IFSC">
                <StyledInput value={mobileBankData.ifsc_code} disabled />
              </InputField>
              <InputField label="Bank name">
                <StyledInput value={mobileBankData.bank_name} disabled />
              </InputField>
              {mobileBankData.name_as_per_bank && (
                <InputField label="Name as per bank">
                  <StyledInput value={mobileBankData.name_as_per_bank} disabled />
                </InputField>
              )}
            </>
          )}
        </div>
      )}

      {bankMode === 'manual' && !mismatchData && (
        <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
          <InputField label="Account number" error={errors.accountNumber}>
            <StyledInput
              value={accountNumber}
              onChange={(e) => {
                setAccountNumber(e.target.value);
                setErrors((p) => ({ ...p, accountNumber: '' }));
              }}
              placeholder="Enter account number"
            />
          </InputField>

          <InputField label="IFSC" error={errors.ifsc}>
            <div className="relative">
              <input
                value={ifscValue}
                onChange={(e) => {
                  setIfsc(e.target.value.toUpperCase());
                  setErrors((p) => ({ ...p, ifsc: '' }));
                }}
                placeholder="e.g. HDFC0001234"
                maxLength={11}
                className={`w-full px-3.5 pr-10 py-2.5 rounded-lg border text-sm transition-all outline-none uppercase
                  ${errors.ifsc ? 'border-red-400' : 'border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'}`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {ifscLookupLoading && (
                  <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                {!ifscLookupLoading && ifscVerified && <FiCheck size={16} className="text-green-500" />}
              </div>
            </div>
          </InputField>

          <InputField label="Bank name" error={errors.bankName}>
            <StyledInput
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder={ifscLookupLoading ? 'Fetching from IFSC…' : 'Auto-filled from IFSC lookup'}
              disabled={ifscLookupLoading}
            />
          </InputField>

          {branchName && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-3.5 py-2.5 text-sm text-green-700">
              Branch: <span className="font-medium">{branchName}</span>
            </div>
          )}
        </div>
      )}

      <GreenButton
        onClick={handleSubmit}
        loading={loading}
        disabled={
          bankMode === 'existing'
            ? !selectedPoolId
            : bankMode === 'mobile'
              ? !mobileDigits || (!mobileBankData && !mobileBankLoading)
              : !accountNumber || !ifscVerified
        }
      >
        Continue <FiArrowRight className="inline ml-1" />
      </GreenButton>
        </>
      )}
    </div>
  );
}

function SupplierDematStep({ supplierId, status, onSuccess }) {
  const pools = status?.existing_pools || {};
  const poolDemats = mapPoolDemats(pools);
  const hasPrefill = poolDemats.length > 0;
  const [dematMode, setDematMode] = useState(hasPrefill ? 'existing' : 'new');
  const [selectedId, setSelectedId] = useState(hasPrefill ? poolDemats[0].id : '');
  const [dematNumber, setDematNumber] = useState('');
  const [dpName, setDpName] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const selected = poolDemats.find((d) => d.id === selectedId);

  const handleSubmit = async () => {
    const e = {};
    if (dematMode === 'existing') {
      if (!selected) e.demat = 'Select a demat account';
    } else {
      const dematCheck = validateDematNumber(dematNumber);
      if (!dematCheck.valid) e.demat = dematCheck.message;
      if (!dpName.trim()) e.dp = 'DP name is required';
    }
    setErrors(e);
    if (Object.keys(e).length) return;

    setLoading(true);
    try {
      const payload =
        dematMode === 'existing' && selected
          ? { demat_number: selected.demat_number, dp_name: selected.dp_name || 'Depository' }
          : { demat_number: dematNumber.trim(), dp_name: dpName.trim() };
      await supplierService.step4Demat(supplierId, payload);
      toast.success('Demat details saved.');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save demat details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Demat details</h3>
        <p className="text-sm text-gray-500 mt-0.5">Demat account number and depository participant name.</p>
      </div>

      {hasPrefill && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">Demat account</p>
          <div className="flex flex-col gap-2.5">
            {[
              { value: 'existing', label: 'Use an existing demat account' },
              { value: 'new', label: 'Add new demat details' },
            ].map((opt) => (
              <label key={opt.value} className="flex items-center gap-2.5 cursor-pointer group">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setDematMode(opt.value)}
                  className={`rounded-full border-2 flex items-center justify-center transition-all cursor-pointer
                    ${dematMode === opt.value ? 'border-green-600 bg-green-600' : 'border-gray-400 bg-white group-hover:border-green-400'}`}
                  style={{ width: 18, height: 18 }}
                >
                  {dematMode === opt.value && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
                <span className={`text-sm cursor-pointer ${dematMode === opt.value ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {dematMode === 'existing' && hasPrefill && (
        <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
          <InputField label="Select demat account" error={errors.demat}>
            <Select
              className="w-full"
              size="large"
              value={selectedId}
              onChange={setSelectedId}
              options={poolDemats.map((d) => ({
                value: d.id,
                label: `${d.dp_name || 'Demat'} — ${d.demat_number}`,
              }))}
            />
          </InputField>
        </div>
      )}

      {(dematMode === 'new' || !hasPrefill) && (
        <div className="flex flex-col gap-5">
          <InputField label="Demat number" error={errors.demat}>
            <StyledInput
              value={dematNumber}
              onChange={(e) => {
                const { value, error } = processDematInputChange(e.target.value);
                setDematNumber(value);
                setErrors((p) => ({ ...p, demat: error || '' }));
              }}
              placeholder="IN12345678901234 or 16-digit CDSL"
              maxLength={DEMAT_MAX_LENGTH}
              className={errors.demat ? 'border-red-400' : ''}
            />
            <p className="mt-1 text-xs text-gray-500">{DEMAT_FORMAT_HINT}</p>
          </InputField>
          <InputField label="DP name" error={errors.dp}>
            <StyledInput
              value={dpName}
              onChange={(e) => {
                setDpName(e.target.value);
                setErrors((p) => ({ ...p, dp: '' }));
              }}
              placeholder="e.g. HDFC Securities"
            />
          </InputField>
        </div>
      )}

      <GreenButton onClick={handleSubmit} loading={loading}>
        Continue <FiArrowRight className="inline ml-1" />
      </GreenButton>
    </div>
  );
}

function isImageFile(f) {
  if (!f) return false;
  if (f.type?.startsWith('image/')) return true;
  return /\.(jpe?g|png)$/i.test(f.name);
}

function isLikelyImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return /\.(jpe?g|png|gif|webp)(\?|#|$)/i.test(url);
}

function getPrimaryCmlDoc(pools) {
  const docs = pools?.cml_docs;
  if (!Array.isArray(docs) || docs.length === 0) return null;
  return docs[0];
}

function SupplierCmlStep({ supplierId, status, onSuccess }) {
  const pools = status?.existing_pools || {};
  const existingDoc = getPrimaryCmlDoc(pools);
  const existingUrl = existingDoc?.document_url || null;
  const hasExistingCml = Boolean(pools.has_cml) || Boolean(existingUrl);

  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!file || !isImageFile(file)) {
      setImagePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleSubmit = async () => {
    if (file) {
      setLoading(true);
      try {
        const fd = new FormData();
        fd.append('file', file, file.name || 'cml');
        await supplierService.step5UploadCml(supplierId, fd);
        toast.success(hasExistingCml ? 'CML replaced.' : 'CML uploaded.');
        onSuccess();
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Failed to upload CML.');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (hasExistingCml) {
      onSuccess();
      return;
    }
    toast.error('Please choose a file to upload.');
  };

  const canContinue = hasExistingCml || Boolean(file);
  const submitLabel = file
    ? hasExistingCml
      ? 'Replace & continue'
      : 'Upload & continue'
    : 'Continue';

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">CML document</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Upload the supplier CML (PDF, JPG, PNG — max 2 MB). You can replace an existing file anytime.
        </p>
      </div>

      {hasExistingCml && !existingDoc && !file && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          A CML document is on file. Use the area below to replace it, or press Continue to proceed without changes.
        </div>
      )}

      {hasExistingCml && existingDoc && !file && (
        <div className="rounded-xl border border-green-200 bg-green-50/80 p-4 flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-gray-800">Current CML on file</p>
            {existingDoc.status && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-800 border border-green-200">
                {String(existingDoc.status).replace(/_/g, ' ')}
              </span>
            )}
          </div>
          {existingUrl && isLikelyImageUrl(existingUrl) && (
            <div className="w-full rounded-lg overflow-hidden border border-green-200/80 bg-white max-h-52 flex items-center justify-center">
              <img src={existingUrl} alt="Current CML document" className="max-h-52 w-full object-contain" />
            </div>
          )}
          {existingUrl && !isLikelyImageUrl(existingUrl) && (
            <a
              href={existingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-green-700 hover:text-green-800 underline-offset-2 hover:underline"
            >
              <HiOutlineDocumentText className="shrink-0" size={18} />
              View current document (opens in new tab)
            </a>
          )}
          {!existingUrl && (
            <p className="text-xs text-gray-600">Document is registered but no preview URL is available.</p>
          )}
          <button
            type="button"
            disabled={loading}
            onClick={() => fileInputRef.current?.click()}
            className="text-sm font-medium text-green-700 hover:text-green-900 w-fit disabled:opacity-50"
          >
            Choose a different file to replace
          </button>
        </div>
      )}

      <div>
        <p className="text-sm font-medium text-gray-700 mb-1">
          {file ? (hasExistingCml ? 'New file (replaces current)' : 'Selected file') : hasExistingCml ? 'Or add / replace from below' : 'File'}
        </p>
        <div
          role="button"
          tabIndex={0}
          onClick={() => !loading && fileInputRef.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center gap-2 cursor-pointer transition-all
            ${file ? 'border-green-400 bg-green-50/50' : 'border-gray-200 bg-gray-50 hover:border-green-400 hover:bg-green-50/30'}`}
        >
          {file ? (
            <div className="flex flex-col items-center gap-3 w-full">
              {imagePreviewUrl ? (
                <div className="w-full rounded-lg overflow-hidden border border-green-200/80 bg-gray-50 max-h-52 flex items-center justify-center">
                  <img
                    src={imagePreviewUrl}
                    alt={`Preview: ${file.name}`}
                    className="max-h-52 w-full object-contain"
                  />
                </div>
              ) : (
                <div
                  className="w-20 h-20 rounded-xl bg-green-100 border border-green-200/70 flex items-center justify-center"
                  aria-hidden
                >
                  <HiOutlineDocumentText className="w-10 h-10 text-green-700" />
                </div>
              )}
              <div className="text-center min-w-0 w-full">
                <p className="text-sm text-gray-700 font-medium break-all">{file.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
            </div>
          ) : (
            <>
              <FiUpload size={20} className="text-gray-400" />
              <p className="text-sm text-gray-500 text-center">
                {hasExistingCml ? 'Click to select a new file' : 'Click to select file'}
              </p>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            if (f.size > 2 * 1024 * 1024) {
              toast.error('File size must be under 2 MB.');
              return;
            }
            setFile(f);
            e.target.value = '';
          }}
        />
        {file && (
          <button
            type="button"
            onClick={() => setFile(null)}
            className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <FiX size={12} /> {hasExistingCml ? 'Keep existing document' : 'Remove file'}
          </button>
        )}
      </div>

      <GreenButton onClick={handleSubmit} loading={loading} disabled={!canContinue}>
        {submitLabel} <FiArrowRight className="inline ml-1" />
      </GreenButton>
    </div>
  );
}

function SupplierCompletePanel({ name, onViewList, onAnother }) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center text-center">
        <div className="w-18 h-18 rounded-full bg-green-600 flex items-center justify-center mb-5 shadow-lg shadow-green-600/30 ring-4 ring-green-100">
          <FiCheck className="w-10 h-10 text-white" strokeWidth={2.5} />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Supplier onboarding complete</h3>
        <p className="text-sm text-gray-500 mt-2 max-w-md leading-relaxed">
          {name ? `${name} is fully onboarded.` : 'This supplier onboarding flow is complete.'}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={onViewList}
          className="w-full py-3 px-4 rounded-xl font-semibold text-sm bg-green-600 text-white hover:bg-green-700 transition-all shadow-md"
        >
          Go to supplier list
        </button>
        <button
          type="button"
          onClick={onAnother}
          className="w-full py-2.5 px-4 rounded-xl font-medium text-sm border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition-all"
        >
          Onboard another supplier
        </button>
      </div>
    </div>
  );
}

export default function SupplierOnboardingPage() {
  const { supplierId: supplierIdParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('edit') === '1';

  const [phase, setPhase] = useState(supplierIdParam ? 'loading' : 'pan_init');
  const [status, setStatus] = useState(null);
  const [serverStepKey, setServerStepKey] = useState('STEP_1_PAN');
  const [completedStepKeys, setCompletedStepKeys] = useState([]);
  const [overrideStepKey, setOverrideStepKey] = useState(null);
  const [donePhase, setDonePhase] = useState(false);

  const loadStatus = useCallback(
    async (id, opts = {}) => {
      const silent = opts.silent === true;
      try {
        const data = await supplierService.getOnboardingStatus(id);
        setStatus(data);
        const finished = isOnboardingFinished(data);
        if (finished) {
          setDonePhase(true);
          setServerStepKey('COMPLETED');
          const keys = buildCompletedKeys(data);
          setCompletedStepKeys([...new Set([...keys, ...WIZARD_STEPS.map((s) => s.key)])]);
        } else {
          setDonePhase(false);
          const sk = serverStepKeyFromStatus(data);
          setServerStepKey(sk);
          setCompletedStepKeys(buildCompletedKeys(data));
        }
        setPhase('wizard');
        return data;
      } catch (err) {
        if (!silent) {
          toast.error(err?.response?.data?.message || 'Failed to load supplier onboarding status.');
          navigate(LIST_PATH, { replace: true });
        }
        throw err;
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (!supplierIdParam) {
      setPhase('pan_init');
      setStatus(null);
      setServerStepKey('STEP_1_PAN');
      setCompletedStepKeys([]);
      setOverrideStepKey(null);
      setDonePhase(false);
      return;
    }
    setPhase('loading');
    loadStatus(supplierIdParam).catch(() => {});
  }, [supplierIdParam, loadStatus]);

  const handleGoBackStep = (stepKey) => {
    if (stepKey === serverStepKey) {
      setOverrideStepKey(null);
    } else {
      setOverrideStepKey(stepKey);
    }
  };

  const advanceAfterStep = useCallback(async () => {
    if (supplierIdParam) {
      const data = await loadStatus(supplierIdParam, { silent: true });
      // Edit mode + just finished: show Completed step, not bank (bank is only the initial edit entry point).
      if (isEditMode && isOnboardingFinished(data)) {
        setOverrideStepKey('COMPLETED');
        return;
      }
    }
    setOverrideStepKey(null);
  }, [supplierIdParam, loadStatus, isEditMode]);

  const handlePanCreated = (newId) => {
    navigate(`${ONBOARD_PATH}/${newId}`, { replace: true });
  };

  const displayStep = overrideStepKey ?? serverStepKey;
  const percentComplete = status?.percentage_complete ?? 0;
  const contextLabel = status?.name || status?.pan || '';
  const wizardTitle = isEditMode && supplierIdParam ? 'Edit supplier' : 'Supplier onboarding';

  const isFullyComplete = donePhase || serverStepKey === 'COMPLETED';
  /** Edit + finished onboarding: stay in wizard and jump steps via sidebar (do not trap on success panel only). */
  const editModeStepNavigation = isEditMode && isFullyComplete;
  const sidebarCurrentStep =
    overrideStepKey ?? (editModeStepNavigation ? EDIT_COMPLETE_DEFAULT_STEP : serverStepKey);
  const formStep =
    editModeStepNavigation ? (overrideStepKey ?? EDIT_COMPLETE_DEFAULT_STEP) : displayStep;

  const renderForm = () => {
    if (isFullyComplete && !isEditMode) {
      return (
        <SupplierCompletePanel
          name={status?.name}
          onViewList={() => navigate(LIST_PATH)}
          onAnother={() => navigate(ONBOARD_PATH, { replace: true })}
        />
      );
    }

    if (phase === 'pan_init' && !supplierIdParam) {
      return (
        <SupplierPanStep status={null} onCreatedId={handlePanCreated} onSuccess={() => {}} />
      );
    }

    if (!supplierIdParam) return null;

    switch (formStep) {
      case 'COMPLETED':
        return (
          <SupplierCompletePanel
            name={status?.name}
            onViewList={() => navigate(LIST_PATH)}
            onAnother={() => navigate(ONBOARD_PATH, { replace: true })}
          />
        );
      case 'STEP_1_PAN':
        return (
          <SupplierPanStep key="pan" status={status} onSuccess={advanceAfterStep} isEditMode={isEditMode} />
        );
      case 'STEP_2_DETAILS':
        return (
          <SupplierPersonalStep
            key="personal"
            supplierId={supplierIdParam}
            status={status}
            onSuccess={advanceAfterStep}
            lockEmailPhone={isEditMode}
          />
        );
      case 'STEP_3_BANK':
        return <SupplierBankStep key="bank" supplierId={supplierIdParam} status={status} onSuccess={advanceAfterStep} />;
      case 'STEP_4_DEMAT':
        return <SupplierDematStep key="demat" supplierId={supplierIdParam} status={status} onSuccess={advanceAfterStep} />;
      case 'STEP_5_CML':
        return <SupplierCmlStep key="cml" supplierId={supplierIdParam} status={status} onSuccess={advanceAfterStep} />;
      default:
        return (
          <div className="flex flex-col gap-4 items-center text-center py-8">
            <FiAlertCircle className="w-10 h-10 text-amber-500" />
            <p className="text-sm text-gray-700">Unknown step: {formStep}</p>
            <GreenButton onClick={() => loadStatus(supplierIdParam, { silent: true })}>Refresh</GreenButton>
          </div>
        );
    }
  };

  if (phase === 'loading' && supplierIdParam) {
    return (
      <DashboardPageWrapper>
        <div className="flex items-center justify-center min-h-0 py-16 sm:min-h-[60vh]">
          <div className="flex flex-col items-center gap-4">
            <svg className="animate-spin h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-sm text-gray-500">Loading supplier onboarding…</p>
          </div>
        </div>
      </DashboardPageWrapper>
    );
  }

  if (phase === 'pan_init' && !supplierIdParam) {
    return (
      <OnboardingWizardLayout
        completedStepKeys={[]}
        currentStepKey="STEP_1_PAN"
        maxReachedKey="STEP_1_PAN"
        contextLabel=""
        percentComplete={0}
        onBackToList={() => navigate(LIST_PATH)}
        wizardTitle={wizardTitle}
      >
        {renderForm()}
      </OnboardingWizardLayout>
    );
  }

  const wizardDone = donePhase || serverStepKey === 'COMPLETED';

  return (
    <OnboardingWizardLayout
      completedStepKeys={completedStepKeys}
      currentStepKey={sidebarCurrentStep}
      maxReachedKey={serverStepKey}
      contextLabel={contextLabel}
      percentComplete={wizardDone ? 100 : percentComplete}
      onBackToList={() => navigate(LIST_PATH)}
      onStepClick={wizardDone && !isEditMode ? undefined : handleGoBackStep}
      wizardTitle={wizardTitle}
    >
      {renderForm()}
    </OnboardingWizardLayout>
  );
}
